// frontend/src/pages/TeamManagementPage.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, Typography, Button, Paper, 
  CircularProgress, Alert, Snackbar,
  Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, IconButton, Tooltip 
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import teamService from '../services/teamService';
import InviteUserModal from '../components/team/InviteUserModal'; 

const TeamManagementPage = () => {
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchMembers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await teamService.getMembers();
      setMembers(response.data);
    } catch (err) {
      setError('Falha ao carregar os membros da equipe. Tente novamente mais tarde.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleRemoveMember = async (memberId) => {
    if (window.confirm('Tem certeza que deseja remover este membro da equipe?')) {
      try {
        await teamService.removeMember(memberId);
        setMembers(prevMembers => prevMembers.filter(m => m.id !== memberId));
        setSnackbar({ open: true, message: 'Membro removido com sucesso!', severity: 'success' });
      } catch (err) {
        setSnackbar({ open: true, message: 'Falha ao remover o membro.', severity: 'error' });
      }
    }
  };

  const handleInviteSuccess = () => {
    fetchMembers(); 
    setIsModalOpen(false); 
    setSnackbar({ open: true, message: 'Convite enviado com sucesso!', severity: 'success' });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const renderContent = () => {
    if (isLoading) {
      return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;
    }

    if (error) {
      return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
    }

    if (members.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', p: 5, color: 'text.secondary' }}>
          <InfoOutlinedIcon sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h6">Nenhum membro na equipe ainda</Typography>
          <Typography>Clique em "Convidar Novo Membro" para começar a montar sua equipe.</Typography>
        </Box>
      );
    }

    return (
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Nome Completo</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Cargo</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.map((member) => (
              <TableRow key={member.id} hover>
                <TableCell component="th" scope="row">{member.nome_completo}</TableCell>
                <TableCell>{member.email}</TableCell>
                <TableCell>{member.cargo || 'Não definido'}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Remover Membro">
                    <IconButton onClick={() => handleRemoveMember(member.id)} color="error" size="small">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Paper elevation={3} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1">
          Gestão de Equipe
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsModalOpen(true)}
        >
          Convidar Novo Membro
        </Button>
      </Box>

      {renderContent()}

      {isModalOpen && (
        <InviteUserModal
          onClose={() => setIsModalOpen(false)}
          onInviteSuccess={handleInviteSuccess} 
        />
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default TeamManagementPage;